/**
 * Wrong-claim attach guard (2026-09-28).
 *
 * Staff attached four lender acknowledgements to the claim they had used
 * ~10 s earlier -- most likely a stale clipboard paste, since those letters
 * look identical and the reference has to be copied from the body. Nothing
 * compared the picked claim with the email's own reference.
 *
 * This module holds the pure comparison and copy so the single-assign pane
 * (TaskDetailPane) and the lender bulk bar (LenderLane) say the same thing.
 * The refs themselves are found server-side with the CAF classifier's own
 * regex (task_lane_service.referenced_claim_refs) -- nothing here parses
 * email text.
 */

/** Tone of an assign-time confirm. `same-client` is the softer one. */
export type AssignConfirmTone = 'mismatch' | 'same-client';

export interface AssignConfirmation {
  tone: AssignConfirmTone;
  title: string;
  lines: string[];
  confirmLabel: string;
}

export type ClaimRefMatch = 'no-refs' | 'match' | 'same-client' | 'mismatch';

export function normaliseClaimRef(ref: unknown): string | null {
  if (typeof ref !== 'string') return null;
  const cleaned = ref.replace(/\s+/g, '').toUpperCase();
  return cleaned || null;
}

/** "CG999001.002" -> "CG999001"; a ref with no suffix is its own base. */
function baseClaimRef(ref: string): string {
  const dot = ref.indexOf('.');
  return dot === -1 ? ref : ref.slice(0, dot);
}

type RefSource = string | null | undefined | ReadonlyArray<string | null | undefined>;

/** Every reference the email carries, AI ref first, de-duplicated. */
export function collectEmailRefs(...sources: RefSource[]): string[] {
  const refs: string[] = [];
  sources.forEach((source) => {
    const values = Array.isArray(source) ? source : [source];
    values.forEach((value) => {
      const ref = normaliseClaimRef(value);
      if (ref && !refs.includes(ref)) refs.push(ref);
    });
  });
  return refs;
}

/**
 * Deploy-order safety. The frontend ships on the main push; the backend
 * that adds aiClaimReference / aiAgreementNumber / subjectClaimRefs /
 * referencedClaimRefs is deployed separately and can land later. Until it
 * does, those keys are simply ABSENT from the payloads.
 *
 * - Absent (or `undefined`): this backend doesn't report the email's
 *   references -- unknown -- so the guard stands aside (no note, no
 *   pre-fill, no confirm) and the picker behaves as it did before the guard.
 * - Present but null / empty: the backend looked and the email has no
 *   reference.
 *
 * JSON never carries `undefined`, so `undefined` here always means the key
 * wasn't sent.
 */
export interface EmailRefs {
  /** False when none of the sources was reported (old backend). */
  known: boolean;
  refs: string[];
}

export function resolveEmailRefs(...sources: RefSource[]): EmailRefs {
  return {
    known: sources.some((source) => source !== undefined),
    refs: collectEmailRefs(...sources),
  };
}

/**
 * Exact ref = match. An email quoting only the base ref ("CG999001") also
 * matches any of that client's claims -- it names the client, not the
 * agreement, so asking would only train staff to click through. The same
 * base with a DIFFERENT suffix is the softer same-client case.
 */
export function matchClaimToEmail(claimId: string, emailRefs: string[]): ClaimRefMatch {
  if (emailRefs.length === 0) return 'no-refs';
  const claim = normaliseClaimRef(claimId) || '';
  const claimBase = baseClaimRef(claim);
  if (emailRefs.some((ref) => ref === claim || (ref === baseClaimRef(ref) && ref === claimBase))) {
    return 'match';
  }
  if (emailRefs.some((ref) => baseClaimRef(ref) === claimBase)) return 'same-client';
  return 'mismatch';
}

/**
 * What to put in the picker's search box. The claim the AI would attach the
 * email to (lender task lists, spec §4.10: one exact reference and a passed
 * identity check) wins; then the AI ref; otherwise the only in-text ref.
 * Several refs and no AI answer: chips, no pre-fill -- we must not guess
 * which one staff meant.
 */
export function pickPrefill(
  aiClaimReference: string | null | undefined,
  emailRefs: string[],
  suggestedClaimRef?: string | null,
): { term: string; chips: string[] } {
  const chips = emailRefs.length > 1 ? emailRefs : [];
  const suggested = normaliseClaimRef(suggestedClaimRef);
  if (suggested) return { term: suggested, chips };
  const ai = normaliseClaimRef(aiClaimReference);
  if (ai) return { term: ai, chips };
  if (emailRefs.length === 1) return { term: emailRefs[0], chips };
  return { term: '', chips };
}

export const PREVIOUS_EMAIL_LINE = 'You attached the previous email to this claim.';
export const NO_REFERENCE_NOTE = 'This email has no claim reference — check the client before assigning.';

export function buildSingleAssignConfirmation(
  claimId: string,
  emailRefs: string[],
  attachedPreviousEmailHere: boolean,
): AssignConfirmation | null {
  const match = matchClaimToEmail(claimId, emailRefs);
  if (match === 'no-refs' || match === 'match') return null;
  const lines = [
    `This email mentions ${emailRefs.join(', ')} but you're attaching it to ${claimId} — attach anyway?`,
  ];
  if (attachedPreviousEmailHere) lines.push(PREVIOUS_EMAIL_LINE);
  return {
    tone: match,
    title: match === 'same-client' ? 'Different claim for the same client?' : "Reference doesn't match",
    lines,
    confirmLabel: 'Attach anyway',
  };
}

export interface RowRefs {
  aiClaimReference?: string | null;
  subjectClaimRefs?: string[] | null;
}

const MAX_LISTED_REFS = 10;

/**
 * Bulk variant: counts the selected tasks whose own reference points at a
 * different claim (same-client suffixes included). Tasks with no reference
 * never count -- same "don't block" rule as the single flow -- and neither
 * do rows whose references weren't reported (old backend, or a checked id
 * no longer on the loaded page). Lists refs, never client names.
 */
export function buildBulkAssignConfirmation(
  claimId: string,
  taskIds: string[],
  rowsById: Record<string, RowRefs | undefined>,
): AssignConfirmation | null {
  const differing: string[] = [];
  let count = 0;
  taskIds.forEach((id) => {
    const row = rowsById[id];
    const { known, refs } = resolveEmailRefs(row?.aiClaimReference, row?.subjectClaimRefs);
    if (!known) return;
    const match = matchClaimToEmail(claimId, refs);
    if (match !== 'mismatch' && match !== 'same-client') return;
    count += 1;
    refs.forEach((ref) => {
      if (!differing.includes(ref) && matchClaimToEmail(claimId, [ref]) !== 'match') differing.push(ref);
    });
  });
  if (count === 0) return null;

  const total = taskIds.length;
  const listed = differing.slice(0, MAX_LISTED_REFS).join(', ');
  const more = differing.length > MAX_LISTED_REFS ? ` and ${differing.length - MAX_LISTED_REFS} more` : '';
  return {
    tone: 'mismatch',
    title: "References don't match",
    lines: [
      `${count} of ${total} selected email${total === 1 ? '' : 's'} mention${count === 1 ? 's' : ''} a different claim to ${claimId}.`,
      `${count === 1 ? 'It mentions' : 'They mention'}: ${listed}${more}`,
    ],
    confirmLabel: 'Assign anyway',
  };
}

// ---------------------------------------------------------------------------
// Recent-attach memory. In-memory, per browser tab: "this user, this
// session" without writing anything anywhere. Lost on reload, by design.
// ---------------------------------------------------------------------------

export const RECENT_ATTACH_WINDOW_MS = 2 * 60 * 1000;

interface AttachRecord {
  claimRef: string;
  taskId: string;
  at: number;
}

let recentAttaches: AttachRecord[] = [];

export function recordClaimAttach(claimId: string, taskIds: string[], now: number = Date.now()): void {
  const claimRef = normaliseClaimRef(claimId);
  if (!claimRef) return;
  recentAttaches = recentAttaches.filter((r) => now - r.at <= RECENT_ATTACH_WINDOW_MS);
  taskIds.forEach((taskId) => recentAttaches.push({ claimRef, taskId, at: now }));
}

/** True when this claim was attached to a DIFFERENT task in the window. */
export function attachedRecentlyElsewhere(claimId: string, taskId: string, now: number = Date.now()): boolean {
  const claimRef = normaliseClaimRef(claimId);
  if (!claimRef) return false;
  return recentAttaches.some(
    (r) => r.claimRef === claimRef && r.taskId !== taskId && now - r.at <= RECENT_ATTACH_WINDOW_MS,
  );
}

/** Test hook. */
export function resetRecentClaimAttaches(): void {
  recentAttaches = [];
}
